import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getUsers } from '@/js/services/api/user/userApi';
import type { ApiUser } from '@/js/services/api/user/types';

type SortableField = 'name' | 'role' | 'lastSeen' | 'hasOverride';

export type UsersTableColumn =
    | { field: SortableField; name: string; sortable: true }
    | { field: 'actions'; name: string; sortable: false };

function mapApiUser(user: ApiUser): UsersTableItem {
    return {
        id: user.id,
        name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email,
        role: user.role?.name ?? '',
        lastSeen: user.lastSeen ? new Date(user.lastSeen) : null,
        hasOverride: user.access?.hasOverride ?? false,
    };
}

export type UsersTableItem = {
    id: string;
    name: string;
    role: string;
    lastSeen: Date | null;
    hasOverride: boolean;
};

export const useUsersTableData = defineStore('usersTableData', () => {
    const users = ref<UsersTableItem[]>([]);

    const search = ref('');
    const sortBy = ref<SortableField>('name');
    const sortDirection = ref<'asc' | 'desc'>('asc');
    const loading = ref(false);

    const tableData = computed(() => {
        let result = [...users.value];

        if (search.value) {
            const term = search.value.toLowerCase();
            result = result.filter(
                (u) => u.name.toLowerCase().includes(term) || u.role.toLowerCase().includes(term)
            );
        }

        result.sort((a, b) => {
            const field = sortBy.value;

            if (field === 'lastSeen') {
                const aTime = a.lastSeen?.getTime() ?? -Infinity;
                const bTime = b.lastSeen?.getTime() ?? -Infinity;

                return sortDirection.value === 'asc' ? aTime - bTime : bTime - aTime;
            }

            if (a[field] < b[field]) return sortDirection.value === 'asc' ? -1 : 1;
            if (a[field] > b[field]) return sortDirection.value === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    });

    function setSort(field: typeof sortBy.value, order: 'asc' | 'desc') {
        sortBy.value = field;
        sortDirection.value = order;
    }

    function removeUser(userId: string) {
        users.value = users.value.filter((user) => user.id !== userId);
    }

    async function fetchUsers() {
        loading.value = true;

        try {
            const pageSize = 100;
            let page = 1;
            let allUsers: UsersTableItem[] = [];

            while (true) {
                const response = await getUsers({ page, pageSize });

                const mapped = response.users.map(mapApiUser);
                allUsers = allUsers.concat(mapped);

                if (response.users.length < pageSize) {
                    break;
                }

                page++;
            }

            users.value = allUsers;
        } finally {
            loading.value = false;
        }
    }

    return {
        users,
        search,
        sortBy,
        sortDirection,
        setSort,
        tableData,
        fetchUsers,
        removeUser,
        loading,
    };
});
